/**
 * Canonical-value resolver — P0-B Sub-Step 2a.
 *
 * Legacy values stored in `user_preferences` (e.g. `auto.makes = ["VW"]`,
 * `auto.models = ["Golf TGI"]`, `immo.object_types = ["Wohnung"]`) need
 * to be re-aligned to the canonical taxonomy when the user opens the
 * cascading filter page. Otherwise the Combobox would silently fail to
 * pre-select the value (the `value` prop wouldn't match any option),
 * effectively losing the user's filter.
 *
 * Resolution strategy (client-side, no backend round-trip):
 *   1. **Exact case-insensitive match** on `value` or `label` of the
 *      canonical option list → confidence 1.0 (silent migrate).
 *   2. **Substring match** ("Golf" matches "Golf VII Variant") →
 *      confidence 0.7 (kept as raw, pill displayed).
 *   3. **No match** → confidence 0.0 (kept as raw, pill displayed).
 *
 * The auto-migrate threshold is 0.8 — exact case-insensitive matches
 * jump straight into the canonical slot. Anything below stays raw
 * and surfaces a "🚨 Nicht canonical"-pill so the user can remove it
 * manually. The Batch-5b-2 one-click "Canonical setzen"-button will
 * use the same resolver under the hood.
 */

/**
 * @typedef {{ value: any, label?: any, [k: string]: any }} CanonicalOption
 */

/**
 * @typedef {{
 *   value: any,
 *   match: any | null,
 *   confidence: number,
 *   migrated: boolean,
 * }} ResolveResult
 */

/**
 * Normalise a value for matching — lowercase + trim + collapse spaces.
 * Diacritics intentionally kept (Škoda ≠ Skoda is a real distinction
 * for some brands' UI labels).
 */
function normalize(s) {
  if (s == null) return "";
  return String(s).toLowerCase().trim().replace(/\s+/g, " ");
}

/**
 * Compare a single legacy value against a canonical option list.
 * @param {any} raw legacy value (string or any scalar)
 * @param {CanonicalOption[]} options canonical option list
 * @param {{labelKey?: string, valueKey?: string}} opts field overrides
 * @returns {ResolveResult}
 */
export function resolveOne(raw, options, opts = {}) {
  const labelKey = opts.labelKey || "label";
  const valueKey = opts.valueKey || "value";
  if (raw == null || raw === "" || !Array.isArray(options) || options.length === 0) {
    return { value: raw, match: null, confidence: 0, migrated: false };
  }
  const n = normalize(raw);

  // Exact case-insensitive match → confidence 1.0
  for (const opt of options) {
    const v = normalize(opt[valueKey]);
    const l = normalize(opt[labelKey]);
    if (v && v === n) return { value: opt[valueKey], match: opt[valueKey], confidence: 1.0, migrated: true };
    if (l && l === n) return { value: opt[valueKey], match: opt[valueKey], confidence: 1.0, migrated: true };
  }

  // Substring match — confidence 0.7, NOT auto-migrated.
  for (const opt of options) {
    const v = normalize(opt[valueKey]);
    const l = normalize(opt[labelKey]);
    if (v && (n.includes(v) || v.includes(n))) {
      return { value: raw, match: opt[valueKey], confidence: 0.7, migrated: false };
    }
    if (l && (n.includes(l) || l.includes(n))) {
      return { value: raw, match: opt[valueKey], confidence: 0.7, migrated: false };
    }
  }

  return { value: raw, match: null, confidence: 0, migrated: false };
}

/**
 * Resolve a list (e.g. `auto.makes`) against a canonical list.
 * Auto-migrate exact matches; keep non-canonical raw values for the pill.
 *
 * @param {any[]} legacyList raw list from user_preferences
 * @param {CanonicalOption[]} options canonical option list
 * @param {{labelKey?: string, valueKey?: string}} opts
 * @returns {{
 *   canonicalList: any[],     // migrated + still-canonical values
 *   nonCanonical: any[],      // values that survived as raw
 *   suggestions: Record<string, any>,   // raw → best-guess match
 *   migratedCount: number,
 * }}
 */
export function resolveList(legacyList, options, opts = {}) {
  const canonical = [];
  const nonCanonical = [];
  const suggestions = {};
  let migratedCount = 0;
  if (!Array.isArray(legacyList) || legacyList.length === 0) {
    return { canonicalList: [], nonCanonical: [], suggestions, migratedCount: 0 };
  }
  for (const raw of legacyList) {
    const r = resolveOne(raw, options, opts);
    if (r.migrated && r.match != null) {
      canonical.push(r.match);
      migratedCount += 1;
    } else if (r.match != null) {
      // Best-guess but below threshold — keep raw, expose suggestion.
      nonCanonical.push(raw);
      suggestions[raw] = r.match;
    } else {
      nonCanonical.push(raw);
    }
  }
  return { canonicalList: canonical, nonCanonical, suggestions, migratedCount };
}

/**
 * Specialisation for legacy CSV-string values (e.g. V1 stored
 * `auto.models = "Golf, Polo"` as a string instead of a list).
 * Returns the same shape as `resolveList`.
 */
export function resolveCsvOrList(legacyValue, options, opts = {}) {
  if (typeof legacyValue === "string") {
    const list = legacyValue.split(",").map((s) => s.trim()).filter(Boolean);
    return resolveList(list, options, opts);
  }
  return resolveList(legacyValue, options, opts);
}

export const __test__ = { normalize };
